import React, { useRef } from "react";
import { useInView } from "react-intersection-observer";
import RuiImage from "../assets/rui.png";
import AnaImage from "../assets/ana.png";
import BeatrizImage from "../assets/beatriz.png";
import AboutImg from "../assets/about.jpg";
import MissionImg from "../assets/mission.jpg";
import ParallaxImg from "../assets/praias.jpg";
import ParallaxImg2 from "../assets/praias2.jpg";
import OurVisionImg from "../assets/vision.jpg";
import Topics from "../components/Topics";
import TeamMembers from "../components/TeamMembers";

// Custom hook for parallax effect
function useParallax(ref, speed = 0.1) {
  React.useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      if (!ticking) {
        window.requestAnimationFrame(() => {
          if (ref.current) {
            const translateValue = Math.min(0, -scrollPosition * speed);
            ref.current.style.transform = `translateY(${translateValue}px)`;
          }
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [ref, speed]);
}

const teamMembers = [
  {
    name: "Rui Simões",
    position: "CEO",
    image: RuiImage,
    link: "https://www.linkedin.com/in/rui-sim%C3%B5es-7756057/",
  },
  {
    name: "Ana Mesquita",
    position: "Chefe de Secção",
    image: AnaImage,
    link: "https://www.linkedin.com/in/ana-paula-mesquita-9391495b/",
  },
  {
    name: "Beatriz Ferreira",
    position: "Técnico Turismo",
    image: BeatrizImage,
    link: "https://www.linkedin.com/in/beatriz-ferreira-94a24b138/",
  },
];

const useSectionViews = () => {
  return {
    history: useInView({ triggerOnce: true, threshold: 0.05 }),
    mission: useInView({ triggerOnce: true, threshold: 0.1 }),
    vision: useInView({ triggerOnce: true, threshold: 0.1 }),
    topics: useInView({ triggerOnce: true, threshold: 0.1 }),
    team: useInView({ triggerOnce: true, threshold: 0.1 }),
    aboutImg: useInView({ triggerOnce: true, threshold: 0.1 }),
    missionImg: useInView({ triggerOnce: true, threshold: 0.1 }),
    visionImg: useInView({ triggerOnce: true, threshold: 0.1 }),
  };
};

const About = () => {
  const parallaxRef1 = useRef(null);
  const parallaxRef2 = useRef(null);
  useParallax(parallaxRef1, 0.15);
  useParallax(parallaxRef2, 0.1);

  const { history, mission, vision, topics, aboutImg, missionImg, visionImg } =
    useSectionViews();

  return (
    <div
      className="relative overflow-x-hidden overflow-y-hidden"
      style={{ minHeight: "100vh" }}
    >
      <div className="relative opacity-100 z-10 md:pt-0">
        {/* Primeiro bloco */}
        <div className="flex flex-col md:flex-row items-center mx-auto px-4 md:px-12 md:pb-16 ">
          <div
            alt="Belle Epoque info"
            ref={history.ref}
            className={`transition-all ${
              history.inView
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-12"
            } duration-0 md:duration-700 ease-in-out w-full`}
          >
            <div className="flex flex-col md:grid md:grid-cols-2 gap-8 md:gap-36 md:px-24 items-center min-h-screen w-full">
              <div className="flex flex-col justify-center h-full items-center mb-8 md:mb-0 w-full">
                <div className="text-justify w-full">
                  <h1 className="text-2xl md:text-3xl font-bold text-center text-cyan-700 mb-6 md:mb-10">
                    A História de BelleEpoque
                  </h1>
                  <p className="text-black mb-4">
                    A Belle Epoque foi criada em março de 2017 para continuar a
                    oferecer serviços de viagens e turismo na Figueira da Foz.
                  </p>
                  <p className="text-black mb-4">
                    O nome &quot;Belle Epoque&quot; foi escolhido não só por
                    representar um dos períodos mais belos da nossa história,
                    mas também por marcar o início do turismo e do lazer que
                    floresceram nessa época tão glamorosa (finais do século XIX
                    até inícios dos anos 20).
                  </p>
                  <p className="text-black mb-4">
                    Em homenagem a esse período áureo, a Belle Epoque foi
                    concebida para que possa tanto &quot;viajar no tempo&quot;
                    quanto explorar destinos modernos que propomos ou que os
                    nossos clientes sugerem.
                  </p>
                  <div className="relative z-10 mt-8 rounded-lg">
                    <blockquote className="text-md italic text-black">
                      “Viajar é como sonhar: é deixar o coração e a mente
                      flutuarem em um mundo de possibilidades.”
                    </blockquote>
                    <p className="text-right text-black mt-2">- Rui Simões</p>
                  </div>
                </div>
              </div>
              <div
                alt="Belle Epoque sobre nós"
                ref={aboutImg.ref}
                className={`flex items-center justify-center w-full mb-8 md:mb-32 h-56 md:h-[500px] ${
                  aboutImg.inView ? "opacity-100" : "opacity-0"
                } translate-x-0 md:transition-all md:duration-700 md:ease-in-out`}
              >
                <img
                  src={AboutImg}
                  alt="About Us"
                  className="object-contain w-full max-w-[350px] md:max-w-[700px] h-full"
                  style={{ background: "#fff" }}
                />
              </div>
            </div>
          </div>
        </div>
        {/* Banner 01 */}
        <div className="hidden md:flex flex-row relative z-10">
          <div className="relative w-full h-96 overflow-hidden">
            <img
              ref={parallaxRef1}
              src={ParallaxImg2}
              alt="About Us"
              className="absolute inset-0 w-full object-cover"
              style={{
                transform: `translateY(0px)`,
                transition: "transform 0.1s linear",
                minWidth: "110%",
              }}
            />
          </div>
        </div>
        <div className="relative flex items-center justify-center mt-64 md:hidden">
          <div className="w-full h-px bg-cyan-700"></div>
          <div className="absolute px-4 bg-cyan-600 rounded-lg">
            <span className="text-white text-lg px-2">✦</span>
          </div>
        </div>
        {/* Segundo bloco */}
        <div className="flex flex-col md:flex-row place-items-center z-10 mx-auto pt-12 md:pt-24 md:py-32">
          <div
            ref={mission.ref}
            className={`relative mx-8 md:mx-36 transition-all ${
              mission.inView
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-12"
            } duration-0 md:duration-700 ease-in-out`}
          >
            <div className="flex flex-col md:grid md:grid-cols-2 gap-8 md:gap-36 items-center w-full">
              <div
                alt="Belle Epoque a nossa missão"
                ref={missionImg.ref}
                className={`flex items-center justify-center w-full mb-8 md:mb-0 h-56 md:h-[500px] md:mt-6 md:transition-all md:duration-700 md:ease-in-out${
                  missionImg.inView ? "opacity-100" : "opacity-0"
                }`}
              >
                <img
                  src={MissionImg}
                  alt="About Us"
                  className="object-contain w-full max-w-[350px] md:max-w-[700px] h-full"
                  style={{ background: "#fff" }}
                />
              </div>
              <div className="flex flex-col justify-center h-full items-center text-justify w-full">
                <h1 className="text-2xl md:text-3xl font-bold text-center mb-6 md:mb-10 text-cyan-700 md:mt-16">
                  A nossa missão
                </h1>
                <p className="text-black mb-4">
                  Na Belle Epoque, a nossa missão é proporcionar experiências de
                  viagem inesquecíveis e transformadoras para todos os nossos
                  clientes. Acreditamos que viajar vai além de visitar novos
                  destinos; é uma oportunidade de descobrir culturas, criar
                  memórias e conectar-se com o mundo de maneiras significativas.
                </p>
                <p className="text-black mb-4">
                  O nosso objetivo é oferecer um serviço personalizado e de alta
                  qualidade, adaptando cada viagem aos desejos e necessidades
                  individuais dos nossos clientes. Para isso, contamos com uma
                  equipa dedicada de especialistas em viagens, sempre prontos
                  para ajudar, desde o planeamento inicial até ao seu regresso a
                  casa.
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* Banner 02 */}
        <div className="hidden md:flex flex flex-row relative z-10">
          <div className="relative w-full h-96 overflow-hidden mt-16">
            <img
              ref={parallaxRef2}
              src={ParallaxImg}
              alt="About Us"
              className="absolute inset-0 w-full object-cover"
              style={{
                transform: `translateY(0px)`,
                transition: "transform 0.1s linear",
                minWidth: "110%",
              }}
            />
          </div>
        </div>
        <div className="z-10 mx-auto py-12">
          <div
            ref={topics.ref}
            className={`container transition-all duration-700 ease-in-out z-10 ${
              topics.inView
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-12"
            }`}
          >
            <div ref={topics.ref} className="z-10 mx-auto py-8">
              <h1 className="text-3xl font-bold text-center text-cyan-700">
                Os nossos valores
              </h1>
            </div>
            <div className="relative z-10 mb-20">
              <Topics />
            </div>
          </div>
        </div>
        <div className="relative flex items-center justify-center mb-24">
          <div className="w-full h-px bg-cyan-700"></div>
          <div className="absolute px-4 bg-cyan-600 rounded-lg">
            <span className="text-white text-lg px-2">✦</span>
          </div>
        </div>
        <div className="place-items-center flex flex-row z-10 mx-auto md:py-12 md:px-24">
          <div>
            <div
              ref={vision.ref}
              className={`px-4 md:px-64 transition-all duration-700 ease-in-out ${
                vision.inView
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-12"
              }`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-36">
                <div className="flex flex-col justify-center h-full items-center text-justify w-full">
                  <h1 className="text-3xl font-bold text-center text-cyan-700 mb-6 md:mt-16 md:mb-10">
                    A nossa visão
                  </h1>
                  <p className="text-black mb-4">
                    Na Belle Epoque, sonhamos em ser a agência de viagens
                    preferida de todos os que buscam explorar o mundo com
                    segurança, conforto e estilo. O nosso objetivo é inspirar
                    pessoas a sair da sua zona de conforto e descobrir a beleza
                    e a diversidade que o mundo tem para oferecer.
                  </p>
                  <p className="text-black mb-4 mb-4">
                    Convidamo-lo a juntar-se a nós nesta jornada e a
                    permitir-nos ajudá-lo a criar memórias que durarão a vida
                    toda.
                  </p>
                  <p className="text-black mb-4">
                    Na Belle Epoque, a sua próxima aventura começa aqui!
                  </p>
                </div>
                <div
                  ref={visionImg.ref}
                  className={`flex items-center justify-center w-full mb-8 md:mb-0 h-56 md:h-[500px] md:mt-6 ${
                    visionImg.inView ? "opacity-100" : "opacity-0"
                  } translate-x-0 md:transition-all md:duration-700 md:ease-in-out`}
                >
                  <img
                    src={OurVisionImg}
                    alt="Our Vision"
                    className="object-contain w-full max-w-[350px] md:max-w-[700px] h-full"
                    style={{ background: "#fff" }}
                  />
                </div>
              </div>
            </div>
            <div className="relative flex items-center justify-center mb-12 mt-40">
              <div className="w-full h-px bg-cyan-700"></div>
              <div className="absolute px-4 bg-cyan-600 rounded-lg">
                <span className="text-white text-lg px-2">✦</span>
              </div>
            </div>
            <div
              className="relative z-10 mb-20"
              alt="Belle Epoque Team Members"
            >
              <TeamMembers teamMembers={teamMembers} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
